import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  ArrowLeft, Building2, FolderOpen, Check, X, RefreshCw, 
  Clock, Calendar, MapPin, FileText, AlertTriangle, Shield,
  ClipboardList, ChevronRight, Bell, CheckCircle2, Eye, 
  ThumbsUp, Clipboard, Activity, Plus, Layers
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const SubcontractorProjectsView = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeProjects, setActiveProjects] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showRequirementsDialog, setShowRequirementsDialog] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [acknowledgments, setAcknowledgments] = useState({ acknowledgedJsas: [], acknowledgedIncidents: [], summary: {} });
  const [loadingAcknowledgments, setLoadingAcknowledgments] = useState(false);
  
  // Division selection state for accepting invitations
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [divisionChoice, setDivisionChoice] = useState('create'); // 'create' or 'existing'
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [newDivisionName, setNewDivisionName] = useState('');
  const [divisionWarning, setDivisionWarning] = useState(null);
  const [checkingDivision, setCheckingDivision] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAcknowledgments();
    fetchDivisions();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, invitationsRes] = await Promise.all([
        axiosInstance.get('/sub/my-projects'),
        axiosInstance.get('/sub/project-invitations')
      ]);
      setActiveProjects(projectsRes.data);
      setPendingInvitations(invitationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await axiosInstance.get('/divisions');
      // Filter out archived and already-linked divisions (with null safety)
      const availableDivisions = (response.data || []).filter(
        d => !d?.isArchived && !d?.gcVisibility
      );
      setDivisions(availableDivisions);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      setDivisions([]); // Set empty array on error
    }
  };

  // Check division status when selecting an existing division
  const checkDivisionStatus = async (divisionId) => {
    if (!divisionId) {
      setDivisionWarning(null);
      return;
    }
    
    setCheckingDivision(true);
    try {
      const response = await axiosInstance.get(`/sub/divisions/${divisionId}/gc-status`);
      const data = response.data;
      
      if (!data.canLinkToGC) {
        setDivisionWarning({
          type: 'error',
          message: data.isAlreadyLinked 
            ? `This division is already linked to "${data.linkedToProject}" (${data.linkedToGC})`
            : 'This division cannot be linked to a GC project.'
        });
      } else if (data.existingData?.hasData) {
        setDivisionWarning({
          type: 'warning',
          message: data.warning,
          data: data.existingData
        });
      } else {
        setDivisionWarning(null);
      }
    } catch (error) {
      console.error('Error checking division status:', error);
    } finally {
      setCheckingDivision(false);
    }
  };

  const fetchAcknowledgments = async () => {
    setLoadingAcknowledgments(true);
    try {
      const response = await axiosInstance.get('/sub/acknowledgments');
      setAcknowledgments(response.data);
    } catch (error) {
      console.error('Error fetching acknowledgments:', error);
      // Don't show error toast - this is secondary data
    } finally {
      setLoadingAcknowledgments(false);
    }
  };

  // Open the accept dialog instead of directly accepting
  const openAcceptDialog = (invitation) => {
    setSelectedInvitation(invitation);
    setNewDivisionName(invitation.projectName); // Pre-fill with project name
    setDivisionChoice('create');
    setSelectedDivisionId('');
    setDivisionWarning(null);
    setShowAcceptDialog(true);
  };

  // Handle division selection change
  const handleDivisionSelect = (divisionId) => {
    setSelectedDivisionId(divisionId);
    checkDivisionStatus(divisionId);
  };

  const handleAcceptInvitation = async () => {
    if (!selectedInvitation) return;
    
    // Validate selection
    if (divisionChoice === 'existing' && !selectedDivisionId) {
      toast.error('Please select a division');
      return;
    }
    if (divisionChoice === 'create' && !newDivisionName.trim()) {
      toast.error('Please enter a division name');
      return;
    }
    
    setProcessing(selectedInvitation.id);
    try {
      const payload = divisionChoice === 'existing' 
        ? { divisionId: selectedDivisionId }
        : { createDivision: true, divisionName: newDivisionName.trim() };
      
      await axiosInstance.post(`/sub/project-invitations/${selectedInvitation.id}/accept`, payload);
      toast.success('Invitation accepted! Division linked to project.');
      setShowAcceptDialog(false);
      setSelectedInvitation(null);
      fetchData();
      fetchDivisions(); // Refresh divisions list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    setProcessing(invitationId);
    try {
      await axiosInstance.post(`/sub/project-invitations/${invitationId}/decline`);
      toast.success('Invitation declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline invitation');
    } finally {
      setProcessing(null);
    }
  };

  // Archive a division (sever ties with GC)
  const handleArchiveDivision = async (divisionId, divisionName, gcProjectName) => {
    if (!confirm(`Are you sure you want to archive "${divisionName}"?\n\nThis will:\n• Remove GC access to this division's data\n• Keep all data intact (read-only)\n• End your participation in "${gcProjectName}"\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setProcessing(divisionId);
    try {
      await axiosInstance.post(`/sub/divisions/${divisionId}/archive`);
      toast.success(`Division "${divisionName}" archived. GC access removed.`);
      fetchData();
      fetchDivisions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive division');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openRequirementsDialog = (project) => {
    setSelectedProject(project);
    setShowRequirementsDialog(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My GC Projects</h1>
              <p className="text-gray-600">View projects you&apos;re linked to as a subcontractor</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/sub/corrective-actions')}>
              <AlertTriangle className="w-4 h-4 mr-2" />
              Corrective Actions
            </Button>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Pending Invitations Banner */}
        {pendingInvitations.length > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100">
                  <Bell className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-orange-800">
                    You have {pendingInvitations.length} pending project invitation{pendingInvitations.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-orange-700">
                    Review and respond to invitations from General Contractors
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <Tabs defaultValue={pendingInvitations.length > 0 ? "invitations" : "active"}>
            <TabsList className="mb-6">
              <TabsTrigger value="active" className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Active Projects ({activeProjects.length})
              </TabsTrigger>
              <TabsTrigger value="invitations" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending Invitations ({pendingInvitations.length})
                {pendingInvitations.length > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center rounded-full">
                    {pendingInvitations.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="acknowledgments" className="flex items-center gap-2">
                <ThumbsUp className="w-4 h-4" />
                GC Acknowledgments ({(acknowledgments.summary?.totalJsasAcknowledged || 0) + (acknowledgments.summary?.totalIncidentsAcknowledged || 0)})
              </TabsTrigger>
            </TabsList>

            {/* Active Projects Tab */}
            <TabsContent value="active">
              {activeProjects.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600">No Active Projects</h3>
                    <p className="text-gray-500 mt-2">
                      You&apos;re not currently linked to any GC projects.
                      <br />
                      Accept an invitation to get started.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {activeProjects.map((link) => (
                    <Card key={link.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <FolderOpen className="w-5 h-5 text-blue-600" />
                              <h3 className="text-lg font-semibold">{link.projectName}</h3>
                              <Badge variant="default" className="bg-green-500">Active</Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Building2 className="w-4 h-4" />
                                <span>GC: {link.gcCompanyName}</span>
                              </div>
                              {link.project?.location && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <MapPin className="w-4 h-4" />
                                  <span>{link.project.location}</span>
                                </div>
                              )}
                              {link.project?.startDate && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Calendar className="w-4 h-4" />
                                  <span>Started: {formatDate(link.project.startDate)}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span>Joined: {formatDate(link.acceptedAt)}</span>
                              </div>
                              {link.divisionName && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Layers className="w-4 h-4 text-purple-500" />
                                  <span>Division: <strong>{link.divisionName}</strong></span>
                                </div>
                              )}
                            </div>

                            {link.project?.description && (
                              <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                                {link.project.description}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col gap-2 ml-4">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openRequirementsDialog(link.project)}
                            >
                              <ClipboardList className="w-4 h-4 mr-1" />
                              Requirements
                            </Button>
                            {link.divisionId && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleArchiveDivision(link.divisionId, link.divisionName, link.projectName)}
                                disabled={processing === link.divisionId}
                              >
                                {processing === link.divisionId ? (
                                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4 mr-1" />
                                )}
                                End Participation
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Quick Stats */}
                        {link.project?.requirements && (
                          <div className="mt-4 pt-4 border-t grid grid-cols-4 gap-4">
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <FileText className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                              <p className="text-xs text-gray-500">Daily JSA</p>
                              <p className="font-medium text-sm">
                                {link.project.requirements.dailyJsaRequired ? 'Required' : 'Optional'}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <AlertTriangle className="w-4 h-4 mx-auto mb-1 text-yellow-500" />
                              <p className="text-xs text-gray-500">Weekly Toolbox</p>
                              <p className="font-medium text-sm">
                                {link.project.requirements.weeklyToolboxTalkRequired ? 'Required' : 'Optional'}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <ClipboardList className="w-4 h-4 mx-auto mb-1 text-purple-500" />
                              <p className="text-xs text-gray-500">Pre-Task Plan</p>
                              <p className="font-medium text-sm">
                                {link.project.requirements.preTaskPlanRequired ? 'Required' : 'Optional'}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <Shield className="w-4 h-4 mx-auto mb-1 text-green-500" />
                              <p className="text-xs text-gray-500">Audit Freq</p>
                              <p className="font-medium text-sm capitalize">
                                {link.project.requirements.auditFrequency || 'Monthly'}
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Pending Invitations Tab */}
            <TabsContent value="invitations">
              {pendingInvitations.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600">No Pending Invitations</h3>
                    <p className="text-gray-500 mt-2">
                      You don&apos;t have any pending project invitations at this time.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pendingInvitations.map((invitation) => (
                    <Card key={invitation.id} className="border-orange-200 hover:border-orange-300 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Building2 className="w-5 h-5 text-orange-600" />
                              <h3 className="text-lg font-semibold">{invitation.projectName}</h3>
                              <Badge variant="outline" className="border-orange-300 text-orange-600">
                                Pending
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Building2 className="w-4 h-4" />
                                <span>From: <strong>{invitation.gcCompanyName}</strong></span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="w-4 h-4" />
                                <span>Invited: {formatDate(invitation.invitedAt)}</span>
                              </div>
                            </div>

                            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                              <p className="font-medium mb-1">What accepting means:</p>
                              <ul className="list-disc list-inside space-y-1 text-blue-700">
                                <li>GC will have read-only visibility of your safety data on this project</li>
                                <li>GC can acknowledge (not approve) your JSAs and incidents</li>
                                <li>You maintain full control of your data</li>
                              </ul>
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 ml-4">
                            <Button 
                              onClick={() => openAcceptDialog(invitation)}
                              disabled={processing === invitation.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              {processing === invitation.id ? 'Processing...' : 'Accept'}
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => handleDeclineInvitation(invitation.id)}
                              disabled={processing === invitation.id}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* GC Acknowledgments Tab */}
            <TabsContent value="acknowledgments">
              {loadingAcknowledgments ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
                </div>
              ) : (acknowledgments.acknowledgedJsas?.length === 0 && acknowledgments.acknowledgedIncidents?.length === 0) ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <ThumbsUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600">No GC Acknowledgments Yet</h3>
                    <p className="text-gray-500 mt-2">
                      When a General Contractor acknowledges your JSAs or Incidents,
                      <br />
                      they will appear here for your records.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-blue-600 font-medium">JSAs Acknowledged</p>
                            <p className="text-3xl font-bold text-blue-700">{acknowledgments.summary?.totalJsasAcknowledged || 0}</p>
                          </div>
                          <div className="p-3 rounded-full bg-blue-200">
                            <FileText className="w-6 h-6 text-blue-700" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-orange-600 font-medium">Incidents Acknowledged</p>
                            <p className="text-3xl font-bold text-orange-700">{acknowledgments.summary?.totalIncidentsAcknowledged || 0}</p>
                          </div>
                          <div className="p-3 rounded-full bg-orange-200">
                            <AlertTriangle className="w-6 h-6 text-orange-700" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Acknowledged JSAs */}
                  {acknowledgments.acknowledgedJsas?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <FileText className="w-5 h-5 text-blue-600" />
                          Acknowledged JSAs
                        </CardTitle>
                        <CardDescription>JSAs that have been acknowledged by General Contractors</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Task Description</TableHead>
                              <TableHead>Work Area</TableHead>
                              <TableHead>Created</TableHead>
                              <TableHead>Acknowledged By</TableHead>
                              <TableHead>Acknowledged At</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {acknowledgments.acknowledgedJsas.map((jsa) => (
                              jsa.gcAcknowledgments?.map((ack, idx) => (
                                <TableRow key={`${jsa.id}-${idx}`}>
                                  {idx === 0 ? (
                                    <>
                                      <TableCell rowSpan={jsa.gcAcknowledgments.length} className="font-medium">
                                        {jsa.taskDescription || 'N/A'}
                                      </TableCell>
                                      <TableCell rowSpan={jsa.gcAcknowledgments.length}>
                                        {jsa.workArea || 'N/A'}
                                      </TableCell>
                                      <TableCell rowSpan={jsa.gcAcknowledgments.length}>
                                        {formatDate(jsa.createdAt)}
                                      </TableCell>
                                    </>
                                  ) : null}
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-4 h-4 text-gray-500" />
                                      <span>{ack.gcCompanyName || 'Unknown GC'}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                      <span>{formatDate(ack.acknowledgedAt)}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}

                  {/* Acknowledged Incidents */}
                  {acknowledgments.acknowledgedIncidents?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <AlertTriangle className="w-5 h-5 text-orange-600" />
                          Acknowledged Incidents
                        </CardTitle>
                        <CardDescription>Incidents that have been acknowledged by General Contractors</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Incident Type</TableHead>
                              <TableHead>Severity</TableHead>
                              <TableHead>Location</TableHead>
                              <TableHead>Created</TableHead>
                              <TableHead>Acknowledged By</TableHead>
                              <TableHead>Acknowledged At</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {acknowledgments.acknowledgedIncidents.map((incident) => (
                              incident.gcAcknowledgments?.map((ack, idx) => (
                                <TableRow key={`${incident.id}-${idx}`}>
                                  {idx === 0 ? (
                                    <>
                                      <TableCell rowSpan={incident.gcAcknowledgments.length} className="font-medium">
                                        {incident.incidentType || 'N/A'}
                                      </TableCell>
                                      <TableCell rowSpan={incident.gcAcknowledgments.length}>
                                        <Badge 
                                          className={
                                            incident.severity === 'Minor' ? 'bg-green-100 text-green-800' :
                                            incident.severity === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                                            incident.severity === 'Serious' ? 'bg-orange-100 text-orange-800' :
                                            incident.severity === 'Severe' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                          }
                                        >
                                          {incident.severity || 'N/A'}
                                        </Badge>
                                      </TableCell>
                                      <TableCell rowSpan={incident.gcAcknowledgments.length}>
                                        {incident.location || 'N/A'}
                                      </TableCell>
                                      <TableCell rowSpan={incident.gcAcknowledgments.length}>
                                        {formatDate(incident.createdAt)}
                                      </TableCell>
                                    </>
                                  ) : null}
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-4 h-4 text-gray-500" />
                                      <span>{ack.gcCompanyName || 'Unknown GC'}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                      <span>{formatDate(ack.acknowledgedAt)}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}

                  {/* Info Note */}
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-green-100">
                          <Eye className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800">About GC Acknowledgments</p>
                          <p className="text-sm text-green-700 mt-1">
                            When a General Contractor acknowledges your JSA or Incident, it means they have reviewed 
                            and noted the document. This is for record-keeping purposes and does not constitute 
                            approval or acceptance of liability. You maintain full ownership of your safety data.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Requirements Dialog */}
        <Dialog open={showRequirementsDialog} onOpenChange={setShowRequirementsDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Project Requirements</DialogTitle>
              <DialogDescription>
                Safety requirements set by the GC for {selectedProject?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedProject?.requirements ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      <span>Daily JSA Required</span>
                    </div>
                    <Badge variant={selectedProject.requirements.dailyJsaRequired ? 'default' : 'secondary'}>
                      {selectedProject.requirements.dailyJsaRequired ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      <span>Weekly Toolbox Talk Required</span>
                    </div>
                    <Badge variant={selectedProject.requirements.weeklyToolboxTalkRequired ? 'default' : 'secondary'}>
                      {selectedProject.requirements.weeklyToolboxTalkRequired ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-purple-500" />
                      <span>Pre-Task Plan Required</span>
                    </div>
                    <Badge variant={selectedProject.requirements.preTaskPlanRequired ? 'default' : 'secondary'}>
                      {selectedProject.requirements.preTaskPlanRequired ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-500" />
                      <span>Audit Frequency</span>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {selectedProject.requirements.auditFrequency || 'Monthly'}
                    </Badge>
                  </div>
                </>
              ) : (
                <p className="text-center text-gray-500 py-4">No specific requirements set for this project.</p>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setShowRequirementsDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Accept Invitation Dialog - Division Selection */}
        <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-green-600" />
                Link Division to Project
              </DialogTitle>
              <DialogDescription>
                Select or create a division for this GC project. Only data from this division will be shared with the GC.
              </DialogDescription>
            </DialogHeader>

            {selectedInvitation && (
              <div className="space-y-4">
                {/* Project Info */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium">Project from GC:</p>
                  <p className="text-lg font-bold text-blue-900">{selectedInvitation.projectName}</p>
                  <p className="text-sm text-blue-700">GC: {selectedInvitation.gcCompanyName}</p>
                </div>

                {/* Privacy Note */}
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Privacy:</strong> The GC will only see JSAs, incidents, and audits from the division you link here. 
                    Your other company data remains private.
                  </p>
                </div>

                {/* Division Choice */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Choose how to link:</Label>
                  <RadioGroup value={divisionChoice} onValueChange={(value) => {
                    setDivisionChoice(value);
                    if (value === 'create') {
                      setDivisionWarning(null);
                      setSelectedDivisionId('');
                    }
                  }}>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                      <RadioGroupItem value="create" id="create" />
                      <Label htmlFor="create" className="flex-1 cursor-pointer">
                        <span className="font-medium">Create New Division</span>
                        <p className="text-sm text-gray-500">Recommended - starts fresh for this project</p>
                      </Label>
                    </div>
                    {divisions.length > 0 && (
                      <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                        <RadioGroupItem value="existing" id="existing" />
                        <Label htmlFor="existing" className="flex-1 cursor-pointer">
                          <span className="font-medium">Use Existing Division</span>
                          <p className="text-sm text-gray-500">Link an existing division's data</p>
                        </Label>
                      </div>
                    )}
                  </RadioGroup>
                </div>

                {/* Create New Division */}
                {divisionChoice === 'create' && (
                  <div className="space-y-2">
                    <Label htmlFor="divisionName">Division Name</Label>
                    <Input
                      id="divisionName"
                      value={newDivisionName}
                      onChange={(e) => setNewDivisionName(e.target.value)}
                      placeholder="Enter division name"
                      className="text-lg"
                    />
                    <p className="text-xs text-gray-500">
                      Must match the GC's project name exactly: <strong>{selectedInvitation.projectName}</strong>
                    </p>
                  </div>
                )}

                {/* Select Existing Division */}
                {divisionChoice === 'existing' && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Select Division</Label>
                      <Select value={selectedDivisionId} onValueChange={handleDivisionSelect}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a division..." />
                        </SelectTrigger>
                        <SelectContent>
                          {divisions.map((div) => (
                            <SelectItem key={div.id} value={div.id}>
                              {div.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Division Warning - Shows when selecting existing division */}
                    {checkingDivision && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Checking division status...
                      </div>
                    )}
                    
                    {divisionWarning && divisionWarning.type === 'error' && (
                      <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                        <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                          <X className="w-4 h-4" />
                          Cannot use this division
                        </p>
                        <p className="text-sm text-red-700 mt-1">{divisionWarning.message}</p>
                      </div>
                    )}
                    
                    {divisionWarning && divisionWarning.type === 'warning' && (
                      <div className="bg-orange-50 border border-orange-300 rounded-lg p-3">
                        <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Warning: Existing Data Will Be Visible
                        </p>
                        <p className="text-sm text-orange-700 mt-1">{divisionWarning.message}</p>
                        <div className="mt-2 text-xs text-orange-600 bg-orange-100 rounded p-2">
                          <strong>By proceeding, you confirm:</strong>
                          <ul className="list-disc ml-4 mt-1">
                            <li>{divisionWarning.data?.jsas} JSA(s) will be shared</li>
                            <li>{divisionWarning.data?.incidents} incident(s) will be shared</li>
                            <li>{divisionWarning.data?.audits} audit(s) will be shared</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowAcceptDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAcceptInvitation}
                disabled={processing}
                className="bg-green-600 hover:bg-green-700"
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Accept & Link Division
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SubcontractorProjectsView;
